import type { FC, ReactElement, ReactNode } from 'react';

import { useRedirectToLobbyCallbackConnected } from '../../hooks/useRedirectToLobbyCallbackConnected';
import { Logger } from '../../logger/Logger';
import { useCoreDispatch } from '../../store/CoreStore';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { usePopupSlice } from '../../store/slices/popup/selectors';
import { popupActions } from '../../store/slices/popup/slice';

import { AutoplayModifiedConnected, autoplayModifiedConnectedOnClose } from './content/AutoplayModifiedConnected';
import { AutoplayStoppedConnected } from './content/AutoplayStoppedConnected';
import { BeforeInactivityConnected } from './content/BeforeInactivityConnected';
import { ConnectionLostConnected, connectionLostOnClose } from './content/ConnectionLostConnected';
import { DontLeaveRoundInProgressConnected } from './content/DontLeaveRoundInProgressConnected';
import { ErrorConnected } from './content/ErrorConnected';
import { ExitConfirmationConnected } from './content/ExitConfirmationConnected';
import { GenericErrorConnected } from './content/GenericErrorContentConnected';
import { GenericErrorNoRefreshConnected } from './content/GenericErrorNoRefreshConnected';
import { InactivityConnected, inactivityConnectedOnClose } from './content/InactivityConnected';
import { InsufficientFundsConnected } from './content/InsufficientFundsConnected';
import { loggedOutClose, LoggedOutConnected } from './content/LoggedOutConnected';
import { NicknameEnterConnected } from './content/NicknameEnterConnected';
import { PrivateTableByInvitationConnected } from './content/PrivateTableByInvitationConnected';
import { PrivateTableDuplicatePlayerConnected } from './content/PrivateTableDuplicatePlayerConnected';
import { PrivateTableOccupiedConnected } from './content/PrivateTableOccupiedConnected';
import { ReconnectingPopup } from './content/Reconnecting';
import { ServerMessageConnected } from './content/ServerMessageConnected';
import { SessionBalanceConnected } from './content/SessionBalanceConnected';
import { SessionBalanceErrorConnected } from './content/SessionBalanceErrorConnected';
import { SessionExpiredConnected } from './content/SessionExpiredConnected';
import { SessionTerminatedConnected } from './content/SessionTerminatedConnected';
import { TableClosedConnected } from './content/TableClosedConnected';
import { VideoIsDisabledConnected } from './content/VideoIsDisabledConnected';
import { VideoStreamQualityConnected } from './content/VideoStreamQualityConnected';
import { VipTableLowBalanceConnected } from './content/VipTableLowBalanceConnected';
import { Popup } from './Popup';
import type { OnCloseCallback } from './types';
import { PopupType } from './types';

export interface PopupConfig {
  content: ReactElement;
  headerLess?: boolean;
  onClose?: OnCloseCallback;
}

const popups: Record<Exclude<PopupType, PopupType.RealityCheck>, PopupConfig> = {
  [PopupType.AutoplayModified]: { content: <AutoplayModifiedConnected />, onClose: autoplayModifiedConnectedOnClose },
  [PopupType.AutoplayStopped]: { content: <AutoplayStoppedConnected /> },
  [PopupType.ByInvitation]: { content: <PrivateTableByInvitationConnected />, headerLess: true },
  [PopupType.ConnectionLost]: { content: <ConnectionLostConnected />, onClose: connectionLostOnClose },
  [PopupType.CustomPopup]: { content: <ServerMessageConnected />, headerLess: true },
  [PopupType.DontLeaveRoundInProgress]: { content: <DontLeaveRoundInProgressConnected />, headerLess: true },
  [PopupType.Error]: { content: <ErrorConnected /> },
  [PopupType.ExitConfirmation]: { content: <ExitConfirmationConnected />, headerLess: true },
  [PopupType.GenericError]: { content: <GenericErrorConnected />, headerLess: true },
  [PopupType.GenericErrorNoRefresh]: { content: <GenericErrorNoRefreshConnected />, headerLess: true },
  [PopupType.Inactivity]: { content: <InactivityConnected />, onClose: inactivityConnectedOnClose },
  [PopupType.InsufficientFunds]: { content: <InsufficientFundsConnected /> },
  [PopupType.LoggedOut]: { content: <LoggedOutConnected />, onClose: loggedOutClose },
  [PopupType.NicknameEnter]: { content: <NicknameEnterConnected />, headerLess: true },
  [PopupType.PrivateTableOccupied]: { content: <PrivateTableOccupiedConnected />, headerLess: true },
  [PopupType.Reconnecting]: { content: <ReconnectingPopup />, headerLess: true },
  [PopupType.SessionBalance]: { content: <SessionBalanceConnected /> },
  [PopupType.SessionBalanceError]: { content: <SessionBalanceErrorConnected /> },
  [PopupType.SessionTerminated]: { content: <SessionTerminatedConnected />, headerLess: true },
  [PopupType.SessionExpired]: { content: <SessionExpiredConnected />, headerLess: true },
  [PopupType.TableClosed]: { content: <TableClosedConnected />, headerLess: true },
  [PopupType.VideoIsDisabled]: { content: <VideoIsDisabledConnected /> },
  [PopupType.VideoStreamQuality]: { content: <VideoStreamQualityConnected /> },
  [PopupType.VipTableLowBalance]: { content: <VipTableLowBalanceConnected />, headerLess: true },
  [PopupType.DuplicatePrivatePlayerSession]: { content: <PrivateTableDuplicatePlayerConnected />, headerLess: true },
  [PopupType.BeforeInactivity]: { content: <BeforeInactivityConnected /> },
};

export interface PopupConnectedProps {
  additionalPopups?: Record<string, PopupConfig>;
}

export const PopupConnected: FC<PopupConnectedProps> = ({ additionalPopups }): ReactNode => {
  const dispatch = useCoreDispatch();
  const redirectToLobby = useRedirectToLobbyCallbackConnected();
  const isGameAbandoned = useGameStateSlice.getIsGameAbandoned();

  const currentPopup = usePopupSlice.currentPopup();
  if (currentPopup) {
    Logger.logEvent('showPopup', {
      additional: currentPopup,
    });
  }

  const allPopups: Record<string, PopupConfig> = { ...popups, ...additionalPopups };
  const popupContent = currentPopup ? allPopups[currentPopup] : null;
  const content = popupContent?.content ?? null;

  const handleClose = (): void => {
    dispatch(popupActions.dismissPopup(currentPopup!));
    if (popupContent?.onClose) {
      popupContent.onClose(dispatch);
    }
    // TODO: This is need because right now navigation is via hook. We need to create event-driven navigation controller to be able to trigger navigation events from plain components
    if (currentPopup === PopupType.SessionBalance || currentPopup === PopupType.SessionBalanceError) {
      redirectToLobby();
    }
  };

  return content ? (
    <Popup
      content={content}
      handleClose={handleClose}
      headerLess={popupContent?.headerLess}
      isAboveAll={isGameAbandoned}
    />
  ) : null;
};
